/**
 * 机器人卖筹码的硬规矩（别人向 AI 求购时它肯不肯卖、最多卖多少）。
 *
 * 服务端与客户端共用这一份：服务端在机器人答应前按它核（room/botSocial.ts 落地前再核一次、
 * ai/social/transferPolicy.ts 决定答不答应），客户端按它算「向这个 AI 最多能买多少」。
 * 以前规矩只在服务端：界面的主按钮要的是「补满」，AI 按规矩几乎必拒 —— 点了就被拒，还不知道为什么。
 *
 * 零依赖：只用协议类型，不 import 任何运行时模块。
 */
import type { AccountId, Chips } from '../ids.js'
import type { RoomConfig } from '../config/roomConfig.js'
import type { BotPersona, TransferRecord, TransferRequestView } from './view.js'

/**
 * 卖完至少要留下「起始筹码 × 这个系数」（且不少于 BOT_SALE.MIN_KEEP_BB 个大盲）。
 * 朋友局里机器人是来陪玩的：赢了钱的机器人应该爽快卖回去，手里不多的也肯匀一部分，
 * 只是别把自己卖到没法打。
 */
export const BOT_KEEP_FACTOR: Readonly<Record<BotPersona, number>> = {
  rock: 0.6,
  tag: 0.5,
  balanced: 0.4,
  agent: 0.4,
  station: 0.35,
  lag: 0.35,
  maniac: 0.3,
}

export const BOT_SALE = {
  MIN_KEEP_BB: 40,
  /** 同一个买家本局最多卖几次 */
  MAX_SALES_PER_BUYER: 2,
} as const

/** 没记性格的机器人（很老的房间）按这个算，与服务端 `botPersonaOf` 同一个默认 */
export const DEFAULT_BOT_PERSONA: BotPersona = 'balanced'

export interface BotSaleFacts {
  /** 机器人能动用的筹码（已扣掉答应了、还没到账的） */
  readonly stack: Chips
  /** 这次的申请额（服务端已钳过） */
  readonly amount: Chips
  /** 本局已卖出总额（含答应了、还没到账的） */
  readonly soldTotal: Chips
  /** 本局已卖给这个买家几次（同上） */
  readonly soldToBuyer: number
}

/** 卖家此刻的处境：`BotSaleFacts` 去掉申请额 */
export type BotSellerStanding = Omit<BotSaleFacts, 'amount'>

export type BotSaleLimit = 'floor' | 'tooMuch' | 'soldOut' | 'repeat'

type SaleConfig = Pick<RoomConfig, 'bigBlind' | 'startingStack'>

/** 卖完至少要留下多少 */
export function botSaleFloor(persona: BotPersona, config: SaleConfig): Chips {
  return Math.max(BOT_SALE.MIN_KEEP_BB * config.bigBlind, Math.round(BOT_KEEP_FACTOR[persona] * config.startingStack))
}

/** 硬规矩挡住了哪一条（没挡住为 null）。顺序即优先级 */
export function botSaleLimit(persona: BotPersona, f: BotSaleFacts, config: SaleConfig): BotSaleLimit | null {
  if (f.stack - f.amount < botSaleFloor(persona, config)) return 'floor'
  // 一次要的超过它筹码的四分之三
  if (f.amount * 4 > f.stack * 3) return 'tooMuch'
  if (f.soldTotal >= config.startingStack) return 'soldOut'
  if (f.soldToBuyer >= BOT_SALE.MAX_SALES_PER_BUYER) return 'repeat'
  return null
}

/**
 * 硬规矩允许的最大申请额；0 = 一个筹码也买不到。
 *
 * ★ 与 `botSaleLimit` 同一口径：1 ≤ a ≤ 返回值的任何申请额都不会被挡，返回值 + 1 必被挡。
 *   两处各写一遍不等式迟早会漂，所以测试逐项对拍（botSale.test.ts）。
 */
export function botSaleMax(persona: BotPersona, s: BotSellerStanding, config: SaleConfig): Chips {
  if (s.soldTotal >= config.startingStack) return 0
  if (s.soldToBuyer >= BOT_SALE.MAX_SALES_PER_BUYER) return 0
  const keepFloor = s.stack - botSaleFloor(persona, config)
  // amount * 4 ≤ stack * 3，申请额是整数
  const share = Math.floor((s.stack * 3) / 4)
  return Math.max(0, Math.min(keepFloor, share))
}

/**
 * 只用全员可见的数据推这个机器人卖家此刻的处境（不含这次的申请额）：
 * 已生效的流水 + 答应了、还没到账的在途申请。答应了的也要算上，与服务端 `botSaleFacts` 同理：
 * 第一笔要到手牌边界才划账，不算它的话第二笔看到的还是卖之前的筹码。
 *
 * ★ 流水只留最近 50 条、重启会清空，服务端的累计则是完整的（从审计表汇总）。
 *   所以这里只可能把上限算**松**，不会算紧：界面挡掉的一定是服务端也会拒的。
 */
export function botSellerStanding(
  seller: { readonly accountId: AccountId; readonly stack: Chips },
  buyerAccountId: AccountId,
  log: readonly Pick<TransferRecord, 'fromAccountId' | 'toAccountId' | 'amount'>[],
  inFlight: readonly Pick<TransferRequestView, 'fromAccountId' | 'toAccountId' | 'amount' | 'status'>[],
): BotSellerStanding {
  let stack = seller.stack
  let soldTotal = 0
  let soldToBuyer = 0
  for (const r of log) {
    if (r.toAccountId !== seller.accountId) continue
    soldTotal += r.amount
    if (r.fromAccountId === buyerAccountId) soldToBuyer++
  }
  for (const r of inFlight) {
    if (r.status !== 'approved' || r.toAccountId !== seller.accountId) continue
    stack -= r.amount
    soldTotal += r.amount
    if (r.fromAccountId === buyerAccountId) soldToBuyer++
  }
  return { stack: Math.max(0, stack), soldTotal, soldToBuyer }
}

/** 客户端一步到位：向这个机器人最多能买多少（只看硬规矩；起始线等其它上限由调用方另外取 min） */
export function botSellerMax(input: {
  readonly persona: BotPersona | null
  readonly seller: { readonly accountId: AccountId; readonly stack: Chips }
  readonly buyerAccountId: AccountId
  readonly config: SaleConfig
  readonly log: readonly Pick<TransferRecord, 'fromAccountId' | 'toAccountId' | 'amount'>[]
  readonly inFlight: readonly Pick<TransferRequestView, 'fromAccountId' | 'toAccountId' | 'amount' | 'status'>[]
}): Chips {
  const standing = botSellerStanding(input.seller, input.buyerAccountId, input.log, input.inFlight)
  return botSaleMax(input.persona ?? DEFAULT_BOT_PERSONA, standing, input.config)
}
